/**
 * Flow field for swarm pathfinding.
 *
 * Runs a single BFS from the player position outward through all
 * walkable tiles.  Each tile stores a direction vector pointing toward
 * the player (i.e. toward the BFS parent).  Swarm enemies look up
 * their current tile to get a maze-aware movement direction.
 *
 * Recomputed only when the player enters a new tile, so per-frame
 * cost is just a typed-array lookup per enemy.
 */
import type { Labyrinth} from './Labyrinth';
import { Tile } from './Labyrinth';

// Cardinal + diagonal directions (dx, dy)
// Cardinals first so they are preferred in narrow corridors.
const DIRS: [number, number][] = [
  [1, 0], [-1, 0], [0, 1], [0, -1],   // cardinal
  [1, 1], [-1, 1], [1, -1], [-1, -1],  // diagonal
];

export class FlowField {
  /** Normalized X direction toward the player for each tile (flat index = y*w+x). */
  readonly dirX: Float32Array;
  /** Normalized Y direction toward the player for each tile. */
  readonly dirY: Float32Array;
  /** BFS distance (in tiles) from the player.  Unreachable = 0xFFFF. */
  readonly dist: Uint16Array;

  readonly w: number;
  readonly h: number;

  /** Last player tile the field was computed from. */
  private lastPx = -1;
  private lastPy = -1;

  // Pre-allocated BFS ring buffer (avoids allocation each recompute).
  private readonly queue: Int32Array;

  constructor(labyrinth: Labyrinth) {
    this.w = labyrinth.width;
    this.h = labyrinth.height;
    const n = this.w * this.h;
    this.dirX = new Float32Array(n);
    this.dirY = new Float32Array(n);
    this.dist = new Uint16Array(n);
    // Worst case every tile is walkable
    this.queue = new Int32Array(n);
  }

  /**
   * Recompute the flow field if the player moved to a new tile.
   * Returns true if a recompute happened.
   */
  update(labyrinth: Labyrinth, playerX: number, playerY: number): boolean {
    const px = Math.floor(playerX);
    const py = Math.floor(playerY);
    if (px === this.lastPx && py === this.lastPy) return false;
    this.lastPx = px;
    this.lastPy = py;
    this.compute(labyrinth, px, py);
    return true;
  }

  /** Force a full recompute (e.g. after building placement changes walkability). */
  invalidate(): void {
    this.lastPx = -1;
    this.lastPy = -1;
  }

  /** Look up the flow direction for a world position. */
  getDir(x: number, y: number): [number, number] {
    const tx = Math.floor(x);
    const ty = Math.floor(y);
    if (tx < 0 || tx >= this.w || ty < 0 || ty >= this.h) return [0, 0];
    const idx = ty * this.w + tx;
    return [this.dirX[idx]!, this.dirY[idx]!];
  }

  // ─── BFS ──────────────────────────────────────────────────

  private compute(lab: Labyrinth, startX: number, startY: number): void {
    const w = this.w;
    const h = this.h;
    const dist = this.dist;
    const dirX = this.dirX;
    const dirY = this.dirY;
    const queue = this.queue;

    // Reset
    dist.fill(0xFFFF);
    dirX.fill(0);
    dirY.fill(0);

    const sk = startY * w + startX;
    dist[sk] = 0;

    let head = 0;
    let tail = 0;
    queue[tail++] = sk;

    while (head < tail) {
      const ck = queue[head++]!;
      const cx = ck % w;
      const cy = (ck - cx) / w;
      const nd = dist[ck]! + 1;

      for (let d = 0; d < 8; d++) {
        const [ddx0, ddy0] = DIRS[d]!;
        const nx = cx + ddx0;
        const ny = cy + ddy0;

        if (nx < 0 || nx >= w || ny < 0 || ny >= h) continue;
        const nk = ny * w + nx;
        if (dist[nk]! <= nd) continue; // already visited with equal or shorter path
        if (lab.tiles[ny]![nx] === Tile.Wall) continue;

        // For diagonals, require both adjacent cardinals to be walkable
        // to prevent corner-cutting through walls.
        if (d >= 4) {
          const ax = cx + ddx0;
          const ay = cy;
          const bx = cx;
          const by = cy + ddy0;
          if (ax < 0 || ax >= w || ay < 0 || ay >= h) continue;
          if (bx < 0 || bx >= w || by < 0 || by >= h) continue;
          if (lab.tiles[ay]![ax] === Tile.Wall || lab.tiles[by]![bx] === Tile.Wall) continue;
        }

        dist[nk] = nd;
        // Direction points FROM neighbor TOWARD player (i.e., toward current cell).
        const ddx = cx - nx; // always -1, 0, or 1
        const ddy = cy - ny;
        // Normalize diagonals
        if (d >= 4) {
          const inv = 1 / Math.SQRT2;
          dirX[nk] = ddx * inv;
          dirY[nk] = ddy * inv;
        } else {
          dirX[nk] = ddx;
          dirY[nk] = ddy;
        }

        queue[tail++] = nk;
      }
    }
  }
}
